/**
 * AgentProjectPage — the V2 agentic chat, rebuilt on Vivideo's OWN planner.
 *
 * The chat talks ONLY to v2-agent-chat (our Claude planner) — never HeyGen. The
 * planner returns one of three things per turn: a plan (→ plan card), a plain
 * answer (→ bubble), or a change request for an already-generated video (→ change
 * card). HeyGen is reached on exactly two presses: Generate (v2-heygen-generate)
 * and Approve-change (the same session, via sendChatMessage).
 *
 * Generate honors the SAME conversion model as the rest of the app:
 *   - FREE users: no real render. A fake "generating" screen (blurred plan cover)
 *     → 1s of playback → paywall. We stash their intent so the moment they pay and
 *     return, the REAL render auto-fires (no second click).
 *   - PRO users: the real render via v2-heygen-generate, with the 14×-duration
 *     progress estimate (GeneratingPlayer), and no fal cover needed.
 * Free users are also capped to a few planner messages (server-side) so the agent
 * can't be farmed as a free chatbot; past the cap we steer them to Generate.
 *
 * Manual (fal) projects resolve through the same load path and render the simple
 * fal result view, so this one page hosts /projects/:id for both kinds. A
 * ChatGPT-style project sidebar (status dots = the projects categories) sits left.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Loader2, Send, PanelLeft, Sparkles, Image as ImageIcon, Film, Music, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/v2/utils';
import { v2Service, type V2AgentProject, type V2VideoPlan, type V2AgentChange, type V2AgentSelectionInput, type V2AgentTurn } from '@/api/v2Service';
import { isLongvidProvider, isProviderRouted } from '@/api/providerEndpoints';
import { TooManyActivePlansError } from '@/api/apiService';
import { useUserStore } from '@/store/userStore';
import { useActiveOrg } from '@/store/workspaceStore';
import { isPaidPlan } from '@/utils/planHelpers';
import { toast } from '@/v2/toast';
import { useUpgradeModal } from '@/v2/upgradeModal';
import { trackPlanCardGenerateClicked, trackFirstGeneration, trackGenerationCompleted, trackGenerationFailed } from '@/services/analyticsService';
import { trackAmpProjectCreated, trackAmpPlanReady, trackAmpFreeChatLimitReached, trackAmpProviderFallback } from '@/services/amplitudeService';
import { videoPlanToCardPlan, type CardPlanSelection } from '@/v2/planMap';
import { calculateHeygenCredits } from '@/utils/constants';
import { V2PlanCard } from '@/v2/components/V2PlanCard';
import { canPlanFullLength } from '@/v2/freeCap';
import { V2ChangeCard } from '@/v2/components/V2ChangeCard';
import { V2StoryboardCard } from '@/v2/components/V2StoryboardCard';
import { GeneratingPlayer } from '@/v2/components/GeneratingPlayer';
import { LitePlanSteps } from '@/v2/components/LitePlanSteps';
import { FalGeneratingPlayer } from '@/v2/components/FalGeneratingPlayer';
import { PreviewReadyGate } from '@/v2/components/PreviewFunnel';
import { ProjectHistorySidebar } from '@/v2/components/ProjectHistorySidebar';
import { VideoResultCard, VideoPlayerModal } from '@/v2/components/VideoPlayerModal';
import { MarkdownText } from '@/v2/components/MarkdownText';
import { BatonLoader } from '@/v2/components/BatonLoader';
import { readOrderPrompt } from '@/v2/seed';
import i18n from '@/i18n';
import { vAlert } from '@/v2/dialogs';
import { isRateable } from '@/v2/videoRating';

const MANUAL_TYPES = new Set(['text_to_video', 'image_to_video', 'first_frame_last_frame', 'multiple_images']);
const POLL_MS = 4000;
// Orders whose provider fallback this tab already reported (see the effect below).
const reportedFallbacks = new Set<string>();
// The captions-free copy is a whole extra render (roughly the film's own time), so
// the CC button's spinner polls for it at this pace and gives up after the bound.
const CLEAN_COPY_POLL_MS = 3000;
const CLEAN_COPY_MAX_MS = 4 * 60 * 1000;
// Free preview chat cap — MUST match v2-agent-chat's FREE_CHAT_MAX_USER_TURNS.
const FREE_CHAT_MAX_USER_TURNS = 2; // initial prompt + 1 follow-up, then steer to Generate

// One-tap edit ideas shown once a video exists. Clicking AUTOFILLS the composer (the
// user tweaks + sends it themselves) — we never auto-send a suggestion.
const FOLLOWUP_SUGGESTIONS = [
  i18n.t('agentProject.followupTranslateSpanish', { ns: 'v2' }),
  i18n.t('agentProject.followupStrongerHook', { ns: 'v2' }),
  i18n.t('agentProject.followupTightenPacing', { ns: 'v2' }),
  i18n.t('agentProject.followupAddMusic', { ns: 'v2' }),
  i18n.t('agentProject.followupVerticalCut', { ns: 'v2' }),
  i18n.t('agentProject.followupVibrantColors', { ns: 'v2' }),
  i18n.t('agentProject.followupCinematic', { ns: 'v2' }),
];

type PlayerVid = { title: string; videoUrl: string; cleanUrl?: string; burnedUrl?: string; poster?: string; duration?: number;
  /** LongVid: this video is the delivered captioned film and its clean copy has not
   *  been made yet — the CC button asks for it (see requestCleanCopy). */
  canRequestClean?: boolean };

/**
 * Production-stage narration, translated. The backend sends a v2 locale KEY
 * ('agentProject.stage.producing') rather than a sentence, so the stage is never
 * English-only — it has landed in either field depending on the order's age, and
 * older rows carry a real English sentence instead. i18next echoes a key back
 * when it has no string for it, so an unknown key falls through to the other
 * field and, failing that, shows nothing: a raw key on screen is worse than a
 * silent loader.
 */
export function stageNarration(stageKey?: string | null, stageText?: string | null): string | null {
  for (const raw of [stageKey, stageText]) {
    let v = (raw || '').trim();
    if (!v) continue;
    if (!/^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)+$/.test(v)) return v;   // a plain sentence
    v = STAGE_KEY_ALIASES[v] ?? v;
    const translated = i18n.t(v, { ns: 'v2' });
    if (translated && translated !== v) return translated;
  }
  return null;
}

/** A stage a provider names differently from the locale key that describes it.
 *  Key → key only: no English ever lives here, and the alias resolves through
 *  the same 30 locale files. */
const STAGE_KEY_ALIASES: Record<string, string> = {
  'agentProject.stage.generating': 'agentProject.stage.producing',
};

/**
 * The render-state fields are named per provider on the order row
 * (`longvid_pct`, `videogen_pct`, …). These read whichever the order carries, so
 * every branch below stays provider-neutral instead of growing a case per twin.
 */
export function renderProgressPct(p?: V2AgentProject | null): number | null | undefined {
  return p?.longvid_pct ?? p?.videogen_pct;
}

export function renderStageNarration(p?: V2AgentProject | null): string | null {
  return stageNarration(
    p?.longvid_stage_key ?? p?.videogen_stage_key,
    p?.longvid_stage_text ?? p?.videogen_stage_text,
  );
}

export function providerAssetNotes(p?: V2AgentProject | null): string[] | null | undefined {
  return p?.longvid_asset_notes ?? p?.videogen_asset_notes;
}

/**
 * A chat turn's body. Anything the backend writes itself (a failure notice, a
 * refusal, a credits warning) arrives as a v2 locale KEY with `text` empty, so
 * the key IS the message and must be resolved or the bubble is blank. A key the
 * locales don't know falls back to whatever text came with it, and never renders
 * as a raw key.
 */
export function turnBody(i18nKey?: string | null, text?: string | null): string | null {
  const key = (i18nKey || '').trim();
  if (key) {
    const translated = i18n.t(key, { ns: 'v2' });
    if (translated && translated !== key) return translated;
  }
  return (text || '').trim() || null;
}

/**
 * The options (avatar / voice / brand) the provider could not carry over, stored
 * on the order as locale keys. The chat shows them as turns; views without a
 * chat resolve them here. An unknown key is dropped — a raw key is worse than
 * saying nothing — while a plain sentence passes through.
 */
export function assetNoteLines(notes?: string[] | null): string[] {
  const out: string[] = [];
  for (const raw of Array.isArray(notes) ? notes : []) {
    const v = (typeof raw === 'string' ? raw : '').trim();
    if (!v) continue;
    const line = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)+$/.test(v) ? turnBody(v, null) : v;
    if (line && !out.includes(line)) out.push(line);
  }
  return out;
}

/**
 * An order's `error_message`, which is a v2 locale KEY on provider orders and
 * raw provider text everywhere else — the same key-or-text rule as a chat turn.
 */
export function errorText(message?: string | null): string | null {
  return turnBody(message, message);
}

// The agent-thinking line cycles through creative phrases (every 4s) so a longer
// plan/answer never feels stuck on one static word.
const THINKING_WORDS = [
  i18n.t('agentProject.thinkingWorkingOnIt', { ns: 'v2' }),
  i18n.t('agentProject.thinkingShapingIdea', { ns: 'v2' }),
  i18n.t('agentProject.thinkingSketchingStoryline', { ns: 'v2' }),
  i18n.t('agentProject.thinkingPicturingScenes', { ns: 'v2' }),
  i18n.t('agentProject.thinkingChoosingWords', { ns: 'v2' }),
  i18n.t('agentProject.thinkingWritingScript', { ns: 'v2' }),
  i18n.t('agentProject.thinkingDesigningVisuals', { ns: 'v2' }),
  i18n.t('agentProject.thinkingSettingTone', { ns: 'v2' }),
  i18n.t('agentProject.thinkingPolishingDetails', { ns: 'v2' }),
  i18n.t('agentProject.thinkingAlmostThere', { ns: 'v2' }),
];

function AgentThinking({ label }: { label?: string | null }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => n + 1), 4000);
    return () => clearInterval(t);
  }, []);
  const word = label || THINKING_WORDS[i % THINKING_WORDS.length];
  return (
    <div className="flex items-center gap-3.5 py-1 animate-fade-in">
      <BatonLoader className="shrink-0" />
      <span key={word} className="text-base font-bold bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] bg-clip-text text-transparent animate-shimmer animate-fade-in">
        {word}
      </span>
    </div>
  );
}

/**
 * Skeleton plan card — rendered the INSTANT the FIRST prompt is in flight (below
 * the branded AgentThinking loader, which stays for everyone), so the plan feels
 * like it's already materializing (the fast free planner answers in ~2-4s;
 * perceived wait ≈ 0). First message only: that response is always a plan card,
 * while follow-up turns can be a question, prose, or a change card — a plan
 * skeleton would over-promise there.
 */
function SkeletonPlanCard() {
  return (
    <div className="rounded-2xl bg-surface-light/60 border border-glass-border p-5 space-y-4 animate-fade-in" aria-hidden>
      <div className="animate-pulse space-y-4">
        {/* Title + meta chips */}
        <div className="h-5 w-2/3 rounded-lg bg-surface-light" />
        <div className="flex gap-2">
          {[0, 1, 2].map((k) => <div key={k} className="h-6 w-20 rounded-full bg-surface-light" />)}
        </div>
        {/* Scene rows */}
        <div className="space-y-2.5 pt-1">
          {[0, 1, 2].map((k) => (
            <div key={k} className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-surface-light shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-1/3 rounded bg-surface-light" />
                <div className="h-3 w-5/6 rounded bg-surface-light" />
              </div>
            </div>
          ))}
        </div>
        {/* Generate placeholder */}
        <div className="h-12 w-full rounded-xl bg-surface-light" />
      </div>
    </div>
  );
}

// The prompt the user submitted for a manual/auto order, shown above the video.
// Prefer the backend value (order_data.prompt — covers EVERY order, past ones and
// other devices) and fall back to the locally-stashed prompt (seed.rememberOrderPrompt)
// so freshly-created orders still show it before the backend field is deployed.
// Long prompts collapse to a few lines with a show-more toggle. Renders nothing
// when neither source has a prompt (e.g. an agent chat order).
function OrderPromptBlock({ orderId, prompt: serverPrompt }: { orderId?: string; prompt?: string | null }) {
  const { t } = useTranslation('v2');
  const [expanded, setExpanded] = useState(false);
  const prompt = (serverPrompt && serverPrompt.trim()) || (orderId ? readOrderPrompt(orderId) : null);
  if (!prompt) return null;
  const long = prompt.length > 220;
  return (
    <div className="rounded-2xl bg-surface-light/60 border border-glass-border p-4">
      <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        <FileText className="w-3.5 h-3.5" /> {t('agentProject.promptLabel')}
      </div>
      <p className={cn('text-sm text-text-secondary whitespace-pre-wrap leading-relaxed', long && !expanded && 'line-clamp-4')}>
        {prompt}
      </p>
      {long && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-80 transition-opacity"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? t('agentProject.promptShowLess') : t('agentProject.promptShowMore')}
        </button>
      )}
    </div>
  );
}

export function AgentProjectPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const { id: orderId } = useParams<{ id: string }>();
  const location = useLocation();
  // orientation rides along from the composer for provider-routed orders (the
  // aspect ratio is locked at creation); absent for everyone else. videoProvider
  // is handed over by any navigation that already had the order row, so the very
  // first load talks to the right backend instead of guessing from the user.
  const seed = (location.state || {}) as { prompt?: string; project?: V2AgentProject; selection?: V2AgentSelectionInput; durationSec?: number; orientation?: string; videoProvider?: string | null };
  const userId = useUserStore((s) => s.userId);
  const user = useUserStore((s) => s.user);
  // Team workspaces: the top-bar pill shows the org's POOLED credits in team
  // context (that's the balance generation actually spends there).
  const activeOrg = useActiveOrg(user);
  const credits = activeOrg ? Math.floor(activeOrg.remainingCredit) : (user?.remainingCredit ?? 0);
  const refreshUser = useUserStore((s) => s.refreshUser);
  // Subscribe to userPlan DIRECTLY (not the isPro() method, whose reference never
  // changes) so this page re-renders the instant a user upgrades mid-session —
  // otherwise the free-chat-limit steer + free funnel gating stay stale after the
  // Stripe return until an unrelated re-render.
  const userPlan = useUserStore((s) => s.user?.userPlan);
  // Inside an ACTIVE team workspace the member is effectively paid — generation
  // spends the org's POOLED credits, not the personal plan. Mirror userStore.isPro()
  // here (which manual/auto modes go through) so the agent doesn't fake-render +
  // paywall a free personal account that's working inside a paid team.
  const isProUser = activeOrg?.orgStatus === 'active' || isPaidPlan(userPlan);

  // "new" = we arrived straight from Home before the project exists. We show the
  // user's prompt + the thinking animation immediately and create the project here
  // (then swap the URL for the real id), so the home screen never has to wait.
  const isNew = orderId === 'new';
  const optimisticNew: V2AgentProject | null = isNew && seed.prompt
    ? { order_id: 'new', order_type: 'heygen_chat', order_status: 'chat_active', order_name: seed.prompt, planning: true, conversation: [{ role: 'user', kind: 'text', text: seed.prompt }] }
    : null;

  const [project, setProject] = useState<V2AgentProject | null>(seed.project ?? optimisticNew);
  // HeyGen declined this request: credits already refunded server-side and the
  // session stopped. The chat is over — no retry from inside this conversation.
  const [refused, setRefused] = useState(false);
  // HeyGen's own latest reply ("I'm on it!", "Now applying your style…"), shown
  // under the loader so a long render never looks like a hang.
  const [providerMessage, setProviderMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(!seed.project && !isNew);
  // The FIRST load has no previous state to fall back on: a swallowed 4xx there
  // left the page spinning for good. Later polls still fail silently.
  const [loadError, setLoadError] = useState(false);
  const [sending, setSending] = useState(isNew);     // planner thinking (creating the first plan)
  const [generating, setGenerating] = useState(false); // Generate/approve in flight
  const [input, setInput] = useState('');
  const [creditsRefreshing, setCreditsRefreshing] = useState(false);
  const [changeResolved, setChangeResolved] = useState<Record<number, 'approved' | 'rejected'>>({});
  // LongVid storyboard cards lock optimistically the same way change cards do.
  const [storyboardResolved, setStoryboardResolved] = useState<Record<number, 'approved' | 'declined'>>({});
  // Open by default on desktop (it pushes the chat aside); CLOSED on mobile, where
  // it becomes a fixed overlay drawer so the chat keeps the full screen width. The
  // route is keyed by :id, so this re-evaluates per project — tapping another
  // project reopens its chat full-screen on phones.
  const [historyOpen, setHistoryOpen] = useState(() => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches);
  // The rich player opens on whichever video the user clicks (each render persists).
  const [playerVideo, setPlayerVideo] = useState<PlayerVid | null>(null);
  // "this plan" click → expand + scroll to that plan card.
  const [expandPlanId, setExpandPlanId] = useState<string | null>(null);
  const [expandTick, setExpandTick] = useState(0);
  // FREE-tier conversion funnel for the Generate press (no real render).
  // 'lite_plan': a tier-2 lite project's stand-in for the plan (no planner call), before 'generating'.
  const [freeFunnel, setFreeFunnel] = useState<null | 'lite_plan' | 'generating' | 'preview'>(null);
  const [freeFunnelImg, setFreeFunnelImg] = useState<string | undefined>(undefined);
  const coverGenRef = useRef<string | null>(null);
  const previewResumeFired = useRef(false);   // free→paid render of an auto/manual preview order
  const introStartedRef = useRef<string | null>(null); // order whose fast intro this mount started
  // Analytics guards: the render-status poll re-runs every few seconds, so completion/
  // failure are each tracked AT MOST ONCE for this mounted project (refs, not state).
  const completedTrackedRef = useRef(false);
  const failedTrackedRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLInputElement>(null);   // focus when a suggestion autofills
  const lastReconcileRef = useRef(0);                   // throttle the stuck-order net

  // The order's own provider routes every call for an EXISTING project. Once the
  // project is loaded its field is authoritative (absent = HeyGen); before that we
  // use whatever the navigation handed us, and `undefined` finally means "unknown",
  // so the user's provider decides and the load self-corrects.
  const loadedOnceRef = useRef(false);
  const projectProvider = project && !isNew ? (project.video_provider ?? null) : seed.videoProvider;
  // Read through a ref inside `refresh` so resolving the provider (seed → the
  // order's own field) does not change the callback's identity and re-fire the
  // mount effect — that would load the project twice on every deep link.
  const providerRef = useRef(projectProvider);
  providerRef.current = projectProvider;
  // The render poll reads the LIVE project through this: a cold load may have had
  // to guess the provider, so the loop must re-check it every tick and switch
  // pairs the moment the order names its owner — never hold a stale closure.
  const projectRef = useRef(project);
  projectRef.current = project;
  const refresh = useCallback(async () => {
    if (!userId || !orderId || isNew) return; // no project to load yet in create mode
    try {
      setProject(await v2Service.getAgentProject(userId, orderId, undefined, providerRef.current));
      loadedOnceRef.current = true;
      setLoadError(false);
    } catch {
      // Keep the last state on a poll; on the first load there is none to keep.
      if (!loadedOnceRef.current) setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [userId, orderId, isNew]);

  useEffect(() => { void refresh(); }, [refresh]);

  // ── The captions-free copy (LongVid) ──────────────────────────────────────
  // LongVid delivers ONE file, captions burned in, so there is nothing for the CC
  // button to swap to. Pressing it has the clean rendition made — a full extra
  // render, free — and this polls until it lands, then feeds it back as the
  // project's (and the open player's) clean url so every later toggle is instant.
  // Each run is numbered: closing the player or leaving the page retires the number
  // and the loop stops instead of writing into an unmounted page.
  const cleanRunRef = useRef(0);
  useEffect(() => () => { cleanRunRef.current += 1; }, []);
  const requestCleanCopy = useCallback(async (): Promise<string | null> => {
    if (!userId || !orderId || isNew) return null;
    const run = ++cleanRunRef.current;
    const deadline = Date.now() + CLEAN_COPY_MAX_MS;
    for (;;) {
      let res: { status: string; video_url?: string | null };
      try {
        res = await v2Service.longvidCleanCopy(userId, orderId, providerRef.current);
      } catch {
        return null; // 'try again later' — the button goes back to plain captions-on
      }
      if (cleanRunRef.current !== run) return null;
      if (res.status === 'ready' && res.video_url) {
        const url = res.video_url;
        setProject((p) => p ? { ...p, video_url: url } : p);
        setPlayerVideo((pv) => pv ? { ...pv, cleanUrl: url } : pv);
        return url;
      }
      // 'none' = this film has no caption to strip: stop offering the button.
      if (res.status === 'none') {
        setProject((p) => p ? { ...p, clean_copy_available: false } : p);
        return null;
      }
      // 'failed' / 'busy' / anything unexpected: give up quietly, try again later.
      if (res.status !== 'rendering' || Date.now() > deadline) return null;
      await new Promise((r) => setTimeout(r, CLEAN_COPY_POLL_MS));
      if (cleanRunRef.current !== run) return null;
    }
  }, [userId, orderId, isNew]);

  // CREATE MODE: fire the first message, then swap /projects/new → /projects/:id.
  const createdRef = useRef(false);
  useEffect(() => {
    if (!isNew || createdRef.current || !userId) return;
    const p = seed.prompt?.trim();
    if (!p) { navigate('/', { replace: true }); return; }
    createdRef.current = true; // synchronous → safe against StrictMode double-mount
    void (async () => {
      try {
        const res = await v2Service.agentChat({
          userId, message: p,
          durationSec: seed.durationSec, orientation: seed.orientation,
          ...(seed.selection || {}),
        });
        // Mode-usage anchor: an agent project now exists (auto/manual fire this
        // from their HomePage submit paths).
        trackAmpProjectCreated({ mode: 'agent', free: !isProUser });
        // replace so Back skips the throwaway /new entry; the keyed route remounts.
        navigate(`/projects/${res.order_id}`, { replace: true, state: { project: res } });
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resp = (e as any)?.response;
        if (e instanceof TooManyActivePlansError) {
          toast.info(t('agentProject.tooManyActivePlans'));
          navigate('/projects', { replace: true });
        } else if (resp?.status === 403 && resp?.data?.error === 'free_project_limit') {
          // Free cap (1 project, lifetime) → back home + the upgrade popup (which
          // offers "Open my project" when one still exists).
          navigate('/', { replace: true });
          useUpgradeModal.getState().show({ kind: 'free_project_limit', existingOrderId: resp.data.existing_order_id, existingVideoProvider: resp.data.video_provider ?? null });
        } else {
          toast.error(t('agentProject.couldNotStart'));
          navigate('/', { replace: true });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, userId]);

  const status = project?.order_status;
  const hasVideo = !!(project?.output_url || project?.video_url);
  // The FIRST plan is generated server-side (async) and lands in the order. While it's in
  // flight the order already exists (so it's in Projects) — we just show the thinking loader
  // and poll until the plan appears, so leaving + returning still picks it up.
  const planning = !!project?.planning;
  // This order is produced by a provider twin, not HeyGen. Every provider branch
  // on this page hangs off this one field — nothing else changes.
  const isRouted = isProviderRouted(project?.video_provider);
  // LongVid alone has a director: on that provider the first plan comes from the
  // provider, not from Vivideo's planner. Everywhere else `isRouted` is the test.
  const isLongvid = isLongvidProvider(project?.video_provider);
  // The newest storyboard / changes card. It stops being "pending" the moment the
  // backend settles it OR its window passes — render_phase can still read
  // 'awaiting_*' after an expiry, and that must not park the composer for good.
  const latestCardTurn = (() => {
    if (!isRouted) return null;   // every other order pays nothing for this scan
    const conversation = project?.conversation || [];
    for (let i = conversation.length - 1; i >= 0; i--) {
      const tn = conversation[i];
      if (tn.kind === 'storyboard' || tn.kind === 'change') return tn;
      // The provider's storyboard now arrives as a PLAN turn (same card as HeyGen's);
      // an approval_id is what makes it a card awaiting an answer.
      if (tn.kind === 'plan' && tn.approval_id) return tn;
    }
    return null;
  })();
  const latestCardExpiresAt = Number(latestCardTurn?.expires_at ?? latestCardTurn?.storyboard?.expires_at) || 0;
  const latestCardPending = !!latestCardTurn && !latestCardTurn.resolved
    && !(latestCardExpiresAt > 0 && Date.now() / 1000 >= latestCardExpiresAt);
  // A storyboard / changes card is on the table and must be answered.
  const providerAwaitingCard = isRouted && String(project?.render_phase || '').startsWith('awaiting_') && latestCardPending;
  // …but only a QUOTE parks the chat. Under a pending storyboard the user may revise the
  // plan in words: the provider's director answers with a new card (the old one is superseded).
  const providerChatLocked = providerAwaitingCard && project?.render_phase === 'awaiting_changes';
  // A message under a pending storyboard IS a director turn (the provider re-plans), so it
  // is sent on HeyGen's background path — a revision must never die at the ~30s gateway cap.
  const providerRevisionTurn = providerAwaitingCard && project?.render_phase === 'awaiting_storyboard';
  // The provider is still mirroring the chosen avatar / voice / brand — the video
  // doesn't exist yet, so this waits like `directing` (and must keep polling even
  // if the order hasn't flipped to in_progress yet).
  const providerPreparing = isRouted && project?.render_phase === 'preparing';
  // LongVid's director asked the customer one question before the storyboard: the chat
  // stays open (the reply is the answer) and the poll stays alive for the card that follows.
  const providerAwaitingInput = isLongvid && project?.render_phase === 'awaiting_input';
  // Drive + watch the two-phase render. The poll advances HeyGen server-side
  // (plan-ready → "generate this video now") and reports the render state; once a
  // video lands (or it fails) we do a full sync. Runs only while in flight.
  useEffect(() => {
    if (!userId || !orderId || isNew) return;
    // Provider orders also poll while chat_active with a card pending — that load poll
    // is the only call that can materialise the card, a reply, or the next video.
    if (status !== 'in_progress' && status !== 'pending' && !providerAwaitingCard && !providerPreparing && !providerAwaitingInput) return;
    let settled = false;
    const tick = async () => {
      // Re-read the provider from the latest project on EVERY tick (see projectRef).
      const live = projectRef.current;
      const tickProvider = live && !isNew ? (live.video_provider ?? null) : seed.videoProvider;
      const tickIsRouted = isProviderRouted(live?.video_provider);
      try {
        // PROVIDER ORDERS: one loop only. There is no HeyGen session to poll, and a
        // second loop would race the delivery claim + burn the shared 10s reconcile
        // budget — so the chat load poll (getAgentProject) drives the whole order.
        if (tickIsRouted) {
          const p = await v2Service.getAgentProject(userId, orderId, undefined, tickProvider);
          setProject(p);
          // The provider narrates its own stage — shown under the loader, exactly
          // like HeyGen's provider message, but translated (see stageNarration).
          const stage = renderStageNarration(p);
          if (stage) setProviderMessage(stage);
          if (!settled && (p.order_status === 'completed' || p.order_status === 'failed')) {
            settled = true;
            const opType = project?.order_type || 'heygen_chat';
            if (p.order_status === 'failed') {
              if (!failedTrackedRef.current && !completedTrackedRef.current) {
                failedTrackedRef.current = true;
                trackGenerationFailed({ operationType: opType, errorMessage: p.error_message ?? undefined });
              }
            } else if (!completedTrackedRef.current && !failedTrackedRef.current) {
              completedTrackedRef.current = true;
              trackGenerationCompleted({ operationType: opType, orderId });
            }
            void refreshUser();
          }
          return;
        }
        const rs = await v2Service.pollRenderStatus(userId, orderId, tickProvider);
        // HeyGen refuses by REPLYING, not erroring — the backend classifies its
        // message, refunds and stops the session, and flags it here. Surface the
        // localized refusal once and stop polling; the chat is over.
        if (rs.refusal && !settled) {
          settled = true;
          setRefused(true);
          void vAlert(i18n.t('moderation.refused', { ns: 'v2' }), {
            title: i18n.t('moderation.refusedTitle', { ns: 'v2' }),
            danger: true,
          });
        }
        // HeyGen's own progress narration, shown while we wait.
        if (rs.provider_message) setProviderMessage(rs.provider_message);
        setProject((p) => p ? {
          ...p,
          // The poll reports the order's OWN provider — adopt it, so a load that had
          // to guess converges here instead of polling the wrong pair for good.
          video_provider: rs.video_provider ?? p.video_provider,
          order_status: rs.order_status ?? p.order_status,
          output_url: rs.output_url ?? p.output_url,
          video_url: rs.video_url ?? p.video_url,
          clean_copy_available: rs.clean_copy_available ?? p.clean_copy_available,
          video_duration: rs.video_duration ?? p.video_duration,
          approved_at: rs.approved_at ?? p.approved_at,
          approved_duration_sec: rs.approved_duration_sec ?? p.approved_duration_sec,
          error_message: rs.error_message ?? p.error_message,
        } : p);
        // Settle on a TERMINAL status. The bare `rs.output_url` fallback stays for a
        // video that lands without a status flip, but it must NOT fire while the order
        // is still in flight: a post-video "Change" re-render keeps the PREVIOUS
        // video's output_url on the order, so the very first poll of the new render
        // used to settle instantly on that stale URL. `rendering` keys on order_status,
        // which is still in_progress — so the progress bar stayed up and held at 99%
        // forever, and settling also silenced the stuck-order reconcile nudge below,
        // the one thing that could have released the credits. The backend poll learned
        // this same lesson (it keys on status, never output_url); match it here.
        const inFlight = rs.order_status === 'in_progress' || rs.order_status === 'pending';
        if (!settled && (rs.order_status === 'completed' || rs.order_status === 'failed' || (rs.output_url && !inFlight))) {
          settled = true;
          // ANALYTICS (GA, fired once per mounted project via refs — this poll re-runs
          // every POLL_MS). A failed status counts as a failure; anything else that
          // settles here (completed, or a video URL landing) counts as completed.
          // This poll serves ALL modes (agent chat, auto, manual/fal) — attribute
          // the settle to the order's real type, not a hardcoded 'heygen_chat'
          // (order_type is immutable per order, so the closure value is safe).
          const settledOpType = project?.order_type || 'heygen_chat';
          if (rs.order_status === 'failed') {
            if (!failedTrackedRef.current && !completedTrackedRef.current) {
              failedTrackedRef.current = true;
              trackGenerationFailed({ operationType: settledOpType, errorMessage: rs.error_message ?? undefined });
            }
          } else if (!completedTrackedRef.current && !failedTrackedRef.current) {
            completedTrackedRef.current = true;
            trackGenerationCompleted({ operationType: settledOpType, orderId });
          }
          void refreshUser();
          void refresh(); // full sync: conversation + final fields
        } else if (!settled) {
          // STUCK-ORDER REFUND NET: a render going FAR too long means HeyGen likely died
          // without firing the completion webhook → the order would sit in_progress and the
          // credits would never come back. Ask the backend to reconcile; it fails + REFUNDS
          // only if HeyGen truly produced no video, and only past its own duration-scaled
          // window (20 min floor, longer for longer videos), so an early call is a harmless
          // no-op. We start nudging at the 20-min floor; throttled to ~once/min.
          const ah = rs.approved_at;
          const startedMs = ah ? Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(ah) ? ah : `${ah}Z`) : NaN;
          if (!Number.isNaN(startedMs) && Date.now() - startedMs > 20 * 60 * 1000 && Date.now() - lastReconcileRef.current > 60_000) {
            lastReconcileRef.current = Date.now();
            try {
              const rec = await v2Service.reconcileStuckOrder(userId, orderId);
              if (rec.reconciled) { settled = true; void refreshUser(); void refresh(); }
            } catch { /* HeyGen unreachable — retry next cycle */ }
          }
        }
      } catch { /* transient — keep polling */ }
    };
    // Pause render-status polling while the tab is backgrounded; resumes on the next tick.
    const t = setInterval(() => { if (!document.hidden) void tick(); }, POLL_MS);
    return () => clearInterval(t);
  }, [status, userId, orderId, isNew, refresh, refreshUser, project?.order_type, isRouted, providerAwaitingCard, providerPreparing, providerAwaitingInput, projectProvider]);

  // PLANNING POLL: the first plan is generated server-side (async) and written into this
  // order. While it's in flight, poll the project until the plan lands (planning clears) —
  // this is what lets the user leave and come back to find the finished plan waiting.
  useEffect(() => {
    if (!userId || !orderId || isNew || !planning) return;
    let stopped = false;
    const tick = async () => {
      try {
        const p = await v2Service.getAgentProject(userId, orderId, undefined, projectProvider);
        if (!stopped) setProject(p);
      } catch { /* transient — keep polling */ }
    };
    const t = setInterval(() => { if (!stopped && !document.hidden) void tick(); }, 2500);
    return () => { stopped = true; clearInterval(t); };
  }, [userId, orderId, isNew, planning, projectProvider]);

  // The provider's stage line comes off the ORDER, so a reload mid-render shows it
  // immediately instead of waiting for the first poll tick to narrate the wait.
  useEffect(() => {
    const stage = renderStageNarration(project);
    if (stage) setProviderMessage(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.longvid_stage_key, project?.longvid_stage_text, project?.videogen_stage_key, project?.videogen_stage_text]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [project?.conversation?.length, sending, generating, planning, freeFunnel]);

  // A LongVid / VideoGen create that HeyGen took over: the order carries fallback_from.
  // Reported once per project per tab, so the A/B can count how often an arm's
  // traffic actually landed on HeyGen, and why.
  useEffect(() => {
    const from = project?.fallback_from;
    if (!from || !orderId || orderId === 'new' || reportedFallbacks.has(orderId)) return;
    reportedFallbacks.add(orderId);
    trackAmpProviderFallback({
      orderId,
      from,
      reason: project?.fallback_reason ?? null,
      mode: project?.generation_mode === 'auto' ? 'auto' : 'agent',
    });
  }, [project?.fallback_from, project?.fallback_reason, project?.generation_mode, orderId]);

  // Free users: adopt a previously-generated cover on reopen (no new spend).
  useEffect(() => {
    if (!isProUser && project?.cover_image_url && !freeFunnelImg) setFreeFunnelImg(project.cover_image_url);
  }, [isProUser, project?.cover_image_url, freeFunnelImg]);

  const refreshCredits = async () => {
    setCreditsRefreshing(true);
    try { await refreshUser(); } finally { setTimeout(() => setCreditsRefreshing(false), 400); }
  };

  const conv = project?.conversation || [];
  const userMsgCount = conv.filter((t) => t.role === 'user').length;
  const freeChatLimitReached = !isProUser && userMsgCount >= FREE_CHAT_MAX_USER_TURNS;

  // ANALYTICS: how long the user actually waited for the FIRST plan (only counts
  // while they stuck around — which is the UX-relevant wait), fired once when the
  // plan lands. Project Created → Plan Ready → generation_started is the funnel
  // that shows plan-wait bounce, and measures the fast-planner lift directly.
  const planWaitStartRef = useRef<number | null>(null);
  const planReadyTrackedRef = useRef(false);
  useEffect(() => {
    const planning = !!project?.planning;
    if (planning && planWaitStartRef.current == null) planWaitStartRef.current = Date.now();
    if (!planning && planWaitStartRef.current != null && !planReadyTrackedRef.current) {
      if (conv.some((tn) => tn.kind === 'plan')) {
        planReadyTrackedRef.current = true;
        trackAmpPlanReady({ waitMs: Date.now() - planWaitStartRef.current });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.planning, conv.length, isProUser]);

  // ANALYTICS: free chat cap hit (composer parked with the Generate steer) — once per mount.
  const chatLimitTrackedRef = useRef(false);
  useEffect(() => {
    if (freeChatLimitReached && !chatLimitTrackedRef.current) {
      chatLimitTrackedRef.current = true;
      trackAmpFreeChatLimitReached();
    }
  }, [freeChatLimitReached]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!userId || !orderId || isNew || !trimmed || sending || generating || planning || !!freeFunnel) return;
    setSending(true);
    setInput('');
    // optimistic echo
    setProject((p) => p ? { ...p, conversation: [...p.conversation, { role: 'user', kind: 'text', text: trimmed }] } : p);
    try {
      // asyncReply: the server stages the turn + answers in the background (planning=true),
      // so a slow agent turn can't be cut off by the API Gateway ~30s ceiling ("Message
      // didn't go through" on turns that actually succeeded). The planning poll picks
      // up the reply when it lands.
      // Provider orders: the backend answers the message itself (the provider's
      // /edits endpoint, or a classified reply) — nothing to background, so no asyncReply.
      // The exception is a revision under a pending storyboard: the director re-plans, which
      // can outlive the gateway cap, and the poll collects the new card / reply either way.
      const backgroundReply = !isRouted || providerRevisionTurn;
      setProject(await v2Service.agentChat({ userId, orderId, message: trimmed, asyncReply: backgroundReply || undefined, videoProvider: projectProvider }));
    } catch {
      toast.error(t('agentProject.messageFailed'));
      setInput(trimmed);
      void refresh();
    } finally {
      setSending(false);
    }
  };

  // The blurred backdrop for the FREE-tier preview funnel only. HeyGen exposes no
  // usable cover URL, so we generate our own from the plan via a cheap fal model
  // (server-side, idempotent per order). Premium users don't get one — their
  // rendering screen is just the blurred gradient (image unnecessary).
  const ensureCover = async (plan: V2VideoPlan) => {
    if (!userId || !orderId || isProUser || freeFunnelImg) return;
    if (project?.cover_image_url) { setFreeFunnelImg(project.cover_image_url); return; }
    if (coverGenRef.current === orderId) return; // at most once per order on the client
    coverGenRef.current = orderId;
    try {
      const res = await v2Service.generatePlanCover({
        userId, orderId,
        title: plan.title || undefined,
        concept: plan.script || undefined,
        style: plan.style || undefined,
        orientation: plan.settings?.orientation || undefined,
      });
      if (res.cover_image_url) setFreeFunnelImg(res.cover_image_url);
    } catch {
      coverGenRef.current = null; // allow a retry on the next trigger
    }
  };

  // (The old reload-reconstruction effect — generate-event + stash heuristics — is gone:
  // a free Generate now flips the order to a real PREVIEW order server-side
  // (recordGenerateEvent markPreview), so the shared preview-funnel effect below rebuilds
  // the gate from order_status alone, exactly like auto/manual.)

  // Remember EXACTLY what they tried to generate, so the moment they subscribe and
  // reopen this project the real render auto-fires — no second Generate click.
  const stashIntent = (avatarId?: string, voiceId?: string, brandRef?: string, durationSec?: number) => {
    try {
      localStorage.setItem(`vivideo_autogen_${orderId}`, JSON.stringify({
        durationSec: durationSec || 30, avatarId: avatarId || null, voiceId: voiceId || null,
        brandRef: brandRef || null, ts: Date.now(),
      }));
    } catch { /* private mode / quota — auto-resume just won't trigger */ }
  };

  const generate = async (plan: V2VideoPlan, avatarId?: string, voiceId?: string, brandRef?: string, planRef?: string) => {
    if (!userId || !orderId || generating || !!freeFunnel) return;
    const dur = Math.round(Number(plan.settings?.duration_sec) || 30);

    // Owner taxonomy: the SUBMIT fired 'Generation Requested'; this press is
    // 'Plan Card Generate Button Clicked'. trackFirstGeneration stays (GA-only
    // activation metric, localStorage-guarded).
    trackPlanCardGenerateClicked('agent');
    trackFirstGeneration('heygen_chat');

    // FREE TIER: run the conversion funnel instead of a real render — a quick
    // "generating" experience, then the blurred preview + paywall. No render API
    // call; we stash the intent + resume after they subscribe.
    // (No paywall event here: PreviewReadyGate fires the 'preview_gate'
    // impression itself when it actually renders, after the 8s funnel.)
    if (!isProUser) {
      setFreeFunnel('generating');
      // Intro flag NOW (not on reload) so a refresh mid-load jumps straight to the
      // gate — same per-order mechanism as the auto/manual funnels.
      try { localStorage.setItem(`vivideo_preview_intro_${orderId}`, '1'); } catch { /* ignore */ }
      void ensureCover(plan);
      stashIntent(avatarId, voiceId, brandRef ?? project?.brand_ref ?? undefined, dur);
      // PERSIST the funnel server-side: the 'generate' chat marker + flipping this order
      // to a PREVIEW order (order_status='preview'). Any reload/return then rebuilds the
      // blurred gate from the order alone — no local-state reconstruction needed.
      void v2Service.recordGenerateEvent(userId, orderId, planRef, { markPreview: true, videoProvider: projectProvider }).then(setProject).catch(() => { /* non-critical */ });
      return;
    }

    setGenerating(true);
    // Flip to the loading player IMMEDIATELY (no dead time on the HeyGen create) — clear
    // any stale video + anchor the progress bar to now, then fire the request. The poll
    // takes over once the session exists; the real approved_at lands on refresh.
    setProject((p) => p ? { ...p, order_status: 'in_progress', output_url: null, video_url: null, approved_at: new Date().toISOString(), approved_duration_sec: dur } : p);
    try {
      // brand_ref also persisted on the order (the lambda falls back to it), so the
      // kit applies whether or not the card surfaced a brand control.
      await v2Service.generateFromPlan({ userId, orderId, plan, avatarId: avatarId || null, voiceId: voiceId || null, brandRef: brandRef ?? project?.brand_ref ?? null, videoProvider: projectProvider });
      void refreshUser();
      // Record "Generating your video from this plan." tagged with the plan it came
      // from, so the chat can link "this plan" → that exact card.
      try { await v2Service.recordGenerateEvent(userId, orderId, planRef, { videoProvider: projectProvider }); } catch { /* non-critical */ }
      await refresh();
    } catch (e) {
      // Roll back the optimistic loader so the plan is editable again.
      setProject((p) => p ? { ...p, order_status: 'chat_active', approved_at: null } : p);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const st = (e as any)?.response?.status;
      if (st === 402) navigate('/subscription?context=insufficient_credits');
      else if (st === 403) {
        // server-side free gate (stale client state) → stash + paywall + auto-resume.
        stashIntent(avatarId, voiceId, brandRef ?? project?.brand_ref ?? undefined, dur);
        navigate(`/subscription?context=video_ready&from_order=${orderId}`);
      } else toast.error(t('agentProject.couldNotStartGeneration'));
    } finally {
      setGenerating(false);
    }
  };

  const approveChange = async (idx: number, change: V2AgentChange, changeId?: string) => {
    if (!userId || !orderId || generating) return;
    setChangeResolved((r) => ({ ...r, [idx]: 'approved' }));
    setGenerating(true);
    // Switch to the loader immediately — HeyGen starts re-rendering on this approve.
    setProject((p) => p ? { ...p, order_status: 'in_progress' } : p);
    try {
      const dur = Math.round(Number(project?.video_duration) || Number(project?.current_plan?.settings?.duration_sec) || 30);
      await v2Service.sendChatMessage(userId, orderId, change.instruction, { autoProceed: true, durationSec: dur });
      void refreshUser();
      // Lock the change card (persisted) + tell the agent it was approved.
      try { await v2Service.resolveChange(userId, orderId, changeId, 'approved', projectProvider); } catch { /* non-critical */ }
      await refresh();
    } catch (e) {
      setChangeResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const st = (e as any)?.response?.status;
      if (st === 402) navigate('/subscription?context=insufficient_credits');
      else toast.error(t('agentProject.couldNotApplyChange'));
    } finally {
      setGenerating(false);
    }
  };

  // PROVIDER ORDERS: the backend answers 200 with the card turn still UNRESOLVED when
  // the answer didn't take (402 on the provider balance, 409 re-read). The optimistic
  // lock must then be dropped, or the card reads "approved" with no way forward.
  const turnSettled = (p: V2AgentProject, turnId: string | undefined, idx: number): boolean => {
    const conversation = p.conversation || [];
    const tn = (turnId ? conversation.find((x) => x.id === turnId) : undefined) ?? conversation[idx];
    return !!tn?.resolved;
  };

  // The provider's approval card is this path's Generate gate: approving charges the
  // Vivideo credits and starts the real production, declining cancels the video.
  // Serves the plan card (today's storyboard) and the older storyboard card alike.
  const approveStoryboard = async (idx: number, approvalId: string, turnId?: string) => {
    if (!userId || !orderId || generating) return;
    trackPlanCardGenerateClicked('agent');
    trackFirstGeneration('heygen_chat');
    setStoryboardResolved((r) => ({ ...r, [idx]: 'approved' }));
    setGenerating(true);
    try {
      const p = await v2Service.longvidApprove(userId, orderId, approvalId, undefined, projectProvider);
      setProject(p);
      if (!turnSettled(p, turnId, idx)) setStoryboardResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
      void refreshUser();
    } catch (e) {
      setStoryboardResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
      // Approving is a charge: a 402 answers in the same shape as the plan card's
      // Generate, so it goes to the same top-up funnel and not a dead toast.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((e as any)?.response?.status === 402) navigate('/subscription?context=insufficient_credits');
      else { toast.error(t('agentProject.couldNotStartGeneration')); void refresh(); }
    } finally {
      setGenerating(false);
    }
  };

  const declineStoryboard = async (idx: number, approvalId: string, turnId?: string) => {
    if (!userId || !orderId || generating) return;
    setStoryboardResolved((r) => ({ ...r, [idx]: 'declined' })); // optimistic lock
    // The lock above is the in-flight guard; `generating` would show the render player.
    try {
      const p = await v2Service.longvidDecline(userId, orderId, approvalId, undefined, projectProvider);
      setProject(p);
      if (!turnSettled(p, turnId, idx)) setStoryboardResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
    } catch {
      setStoryboardResolved((r) => { const n = { ...r }; delete n[idx]; return n; }); // revert → allow retry
      void refresh();
    }
  };

  // A provider's changes quote: the same two endpoints as the storyboard card, so the
  // approve does NOT go through HeyGen's sendChatMessage.
  const approveProviderChange = async (idx: number, approvalId: string, turnId?: string) => {
    if (!userId || !orderId || generating) return;
    setChangeResolved((r) => ({ ...r, [idx]: 'approved' }));
    setGenerating(true);
    try {
      const p = await v2Service.longvidApprove(userId, orderId, approvalId, undefined, projectProvider);
      setProject(p);
      if (!turnSettled(p, turnId, idx)) setChangeResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
      void refreshUser();
    } catch (e) {
      setChangeResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
      // Same charge, same 402 shape, same top-up funnel as Generate.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((e as any)?.response?.status === 402) navigate('/subscription?context=insufficient_credits');
      else { toast.error(t('agentProject.couldNotApplyChange')); void refresh(); }
    } finally {
      setGenerating(false);
    }
  };

  const declineProviderChange = async (idx: number, approvalId: string, turnId?: string) => {
    if (!userId || !orderId || generating) return;
    setChangeResolved((r) => ({ ...r, [idx]: 'rejected' })); // optimistic lock
    // Not `generating`: that flag is the render player, and a decline renders nothing
    // (the HeyGen discard locks the card the same way and shows no player).
    try {
      const p = await v2Service.longvidDecline(userId, orderId, approvalId, undefined, projectProvider);
      setProject(p);
      if (!turnSettled(p, turnId, idx)) setChangeResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
    } catch {
      setChangeResolved((r) => { const n = { ...r }; delete n[idx]; return n; });
      void refresh();
    }
  };

  const rejectChange = async (idx: number, changeId?: string) => {
    if (!userId || !orderId) return;
    setChangeResolved((r) => ({ ...r, [idx]: 'rejected' })); // optimistic lock
    try {
      // Persist the discard + tell the agent (so it never re-activates + knows).
      setProject(await v2Service.resolveChange(userId, orderId, changeId, 'discarded', projectProvider));
    } catch {
      setChangeResolved((r) => { const n = { ...r }; delete n[idx]; return n; }); // revert → allow retry
      void refresh();
    }
  };

  // "this plan" click → expand that plan card + scroll it into view.
  const scrollToPlan = (planRef?: string | null) => {
    if (!planRef) return;
    setExpandPlanId(planRef);
    setExpandTick((n) => n + 1);
    setTimeout(() => document.getElementById(`plan-${planRef}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  };

  // Persist a manually-edited plan: optimistically swap the plan turn + current_plan,
  // then send it to the planner (becomes the current plan → what Generate renders).
  const saveEditedPlan = async (turnIdx: number, edited: V2VideoPlan) => {
    if (!userId || !orderId) return;
    setProject((p) => p ? {
      ...p,
      current_plan: edited,
      conversation: p.conversation.map((t, i) => (i === turnIdx ? { ...t, plan: edited } : t)),
    } : p);
    try {
      setProject(await v2Service.saveEditedPlan(userId, orderId, edited, projectProvider));
      toast.success(t('agentProject.planUpdated'));
    } catch {
      toast.error(t('agentProject.couldNotSaveEdits'));
      void refresh();
    }
  };

  // An in-card avatar/voice/brand pick → persist to THIS plan card's own turn (PER-CARD
  // cast), keyed by its planRef, so generating from any card uses ITS cast — never the
  // latest/global value. Optimistic locally, then saved server-side (empty-message load →
  // _apply_selection_to_turn). A new card with no pick falls back to the project default.
  const persistSelection = async (planRef: string | undefined, patch: Partial<CardPlanSelection>) => {
    if (!userId || !orderId) return;
    const map = {
      ...(patch.avatarId !== undefined ? { avatar_id: patch.avatarId } : {}),
      ...(patch.avatarName !== undefined ? { avatar_name: patch.avatarName } : {}),
      ...(patch.avatarImage !== undefined ? { avatar_image: patch.avatarImage } : {}),
      ...(patch.voiceId !== undefined ? { voice_id: patch.voiceId } : {}),
      ...(patch.voiceName !== undefined ? { voice_name: patch.voiceName } : {}),
      ...(patch.voicePreviewUrl !== undefined ? { voice_preview_url: patch.voicePreviewUrl } : {}),
      ...(patch.brandRef !== undefined ? { brand_ref: patch.brandRef } : {}),
      ...(patch.brandName !== undefined ? { brand_name: patch.brandName } : {}),
      ...(patch.brandLogo !== undefined ? { brand_logo: patch.brandLogo } : {}),
    };
    // Optimistic: stamp THIS plan turn's cast (matched by planRef = the plan's id).
    setProject((p) => p ? {
      ...p,
      conversation: p.conversation.map((t) => (planRef && t.id === planRef ? { ...t, ...map } : t)),
    } : p);
    try { setProject(await v2Service.getAgentProject(userId, orderId, { ...patch, planRef }, projectProvider)); } catch { /* keep optimistic */ }
  };

  // DEDUPE display of duplicate videos: an accidental double-generate (fixed
  // server-side, but already baked into some saved conversations) leaves TWO
  // adjacent 'video' turns with no user/change turn between them. Legitimate
  // re-renders always have a 'change' (or user) turn between videos, so we only
  // hide a video turn when a LATER video turn follows it with nothing but other
  // videos in between — i.e. keep just the last video in each uninterrupted run.
  const skipVideoIdx = (() => {
    const skip = new Set<number>();
    for (let i = 0; i < conv.length; i++) {
      if (conv[i].kind !== 'video') continue;
      for (let j = i + 1; j < conv.length; j++) {
        if (conv[j].kind === 'video') { skip.add(i); break; }              // a later video in this run → drop the earlier
        if (conv[j].role === 'user' || conv[j].kind === 'change') break;   // real boundary → keep this video
      }
    }
    return skip;
  })();

  const latestPlanIdx = (() => { for (let i = conv.length - 1; i >= 0; i--) if (conv[i].kind === 'plan') return i; return -1; })();
  const latestChangeIdx = (() => { for (let i = conv.length - 1; i >= 0; i--) if (conv[i].kind === 'change') return i; return -1; })();
  const latestVideoIdx = (() => { for (let i = conv.length - 1; i >= 0; i--) if (conv[i].kind === 'video') return i; return -1; })();
  // A change is "resolved" if the backend persisted it on the turn (survives reload
  // → never re-activates) OR we just acted on it optimistically. Backend uses
  // 'discarded'; the card speaks 'rejected'.
  const changeStateAt = (i: number, t: V2AgentTurn): 'approved' | 'rejected' | null => {
    if (t.resolved === 'approved') return 'approved';
    // LongVid quotes settle as 'declined'/'expired'; both read as a discarded card.
    if (t.resolved === 'discarded' || t.resolved === 'declined' || t.resolved === 'expired') return 'rejected';
    return changeResolved[i] ?? null;
  };
  const latestStoryboardIdx = (() => { for (let i = conv.length - 1; i >= 0; i--) if (conv[i].kind === 'storyboard') return i; return -1; })();
  const storyboardStateAt = (i: number, t: V2AgentTurn): 'approved' | 'declined' | 'expired' | 'superseded' | null => {
    if (t.resolved === 'approved' || t.resolved === 'declined' || t.resolved === 'expired') return t.resolved;
    // The director replaced this plan with a revised one: a past card, never pending.
    if (t.resolved === 'superseded') return 'superseded';
    if (t.resolved === 'discarded') return 'declined';
    return storyboardResolved[i] ?? null;
  };
  // The latest change is awaiting the user's approve/discard → park the composer +
  // hold off plan generation until they respond (same blocker as during a render).
  const awaitingChange = latestChangeIdx >= 0 && hasVideo && !changeStateAt(latestChangeIdx, conv[latestChangeIdx]);
  const isManual = !!project && project.order_type !== 'heygen_chat' && MANUAL_TYPES.has(String(project.order_type));
  // A render is in flight whenever the order is in_progress/pending — for the FIRST
  // generate (no video yet) AND for a post-video CHANGE re-render (the old video is
  // still on the order). Both must show only the loading view, so we do NOT gate on
  // !hasVideo. Completion flips status→completed, which clears this.
  // Also key off `generating` so the loading player is up the INSTANT Generate/Approve
  // is pressed — before the HeyGen session-create round-trip returns (no dead wait).
  const rendering = generating || status === 'in_progress' || status === 'pending' || providerPreparing;
  // The provider's director can be mid-turn (replying, or quoting a change) with
  // nothing rendering yet. Kept INSIDE `rendering` so the composer and the cards
  // stay frozen either way — only the loader below differs.
  // Only LongVid has a director; no other provider ever reports this phase.
  const directing = isLongvid && project?.render_phase === 'directing';
  // Mirroring the cast into the provider — same parked chat, its own stage line.
  const preparing = providerPreparing;
  const planDurationSec = Number(project?.current_plan?.settings?.duration_sec) || 30;
  const videoUrl = project?.output_url || project?.video_url || '';
  // Offer the CC button on a delivered film that HAS captions while both urls are
  // still the one captioned file — pressing it has the clean copy rendered.
  const cleanCopyOffered = !!project?.clean_copy_available && !!project?.output_url
    && project?.video_url === project?.output_url;
  const title = project?.order_name || seed.prompt || t('agentProject.untitledVideo');
  // Auto-generate (one-shot HeyGen) → manual-style view: no chat, just the 16×
  // loader → the finished video (the project sidebar stays, rendered above).
  const isAuto = project?.generation_mode === 'auto';

  // ── AUTO-RESUME GENERATION (free → paid conversion) ──────────────────────
  // If the user pressed Generate while free, the intent was stashed + the paywall
  // shown. The instant they're Pro and back here with the plan loaded, fire the
  // REAL render automatically — so they land on a live "generating" preview.
  const autoGenFired = useRef(false);
  const liteReloadRef = useRef(false);
  useEffect(() => {
    if (autoGenFired.current) return;
    // AGENT-mode only: auto/manual preview orders resume via the preview-resume
    // effect below (their params live on the order) — never both, or we'd double-render.
    if (!orderId || isNew || loading || isManual || isAuto) return;
    let intent: { durationSec?: number; avatarId?: string | null; voiceId?: string | null; brandRef?: string | null; ts?: number } | null = null;
    try { const raw = localStorage.getItem(`vivideo_autogen_${orderId}`); if (raw) intent = JSON.parse(raw); } catch { intent = null; }
    if (intent?.ts && Date.now() - intent.ts > 24 * 60 * 60 * 1000) { // stale → drop
      try { localStorage.removeItem(`vivideo_autogen_${orderId}`); } catch { /* ignore */ }
      intent = null;
    }
    // No stash (private mode / cleared / another device)? The Generate press itself is
    // durable now — it flipped the order to 'preview' — so resume from the order's own
    // cast. Never fires for plain chat_active orders (no Generate was ever pressed).
    const isAgentPreview = !!(project?.is_preview || project?.order_status === 'preview');
    if (!intent && isAgentPreview) {
      intent = { avatarId: project?.avatar_id, voiceId: project?.voice_id, brandRef: project?.brand_ref };
    }
    if (!intent) return;
    if (!isProUser) return;                                   // still free (mid-payment) → wait
    const plan = project?.current_plan;
    if (!plan) return;                                        // plan not loaded yet → wait
    // Tier-2 lite project: its stand-in plan is never rendered. The first load as a paid
    // user starts the real plan server-side (planning poll) — reload once if this page
    // was loaded while still free; the effect re-runs when the plan lands.
    if (plan.lite_placeholder) {
      if (!liteReloadRef.current) { liteReloadRef.current = true; void refresh(); }
      return;
    }
    if (rendering || hasVideo) {                              // already rendering/done → consume
      try { localStorage.removeItem(`vivideo_autogen_${orderId}`); } catch { /* ignore */ }
      autoGenFired.current = true;
      return;
    }
    if (generating || sending || freeFunnel) return;          // transient → wait
    autoGenFired.current = true;
    try { localStorage.removeItem(`vivideo_autogen_${orderId}`); } catch { /* ignore */ }
    void generate(plan, intent.avatarId || undefined, intent.voiceId || undefined, intent.brandRef || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProUser, loading, isManual, isAuto, orderId, project?.current_plan, project?.is_preview, project?.order_status, rendering, hasVideo, generating, sending, freeFunnel]);

  // FREE→PAID RESUME for auto/manual PREVIEW orders: the instant the user is Pro and back on
  // a still-preview project, render it for real on the SAME order (no duplicate). The params
  // live on the order, so no stash is needed. This auto-spends credits on return (intended).
  useEffect(() => {
    if (previewResumeFired.current || !userId || !orderId || isNew || loading || !isProUser) return;
    const isPreview = !!(project?.is_preview || project?.order_status === 'preview');
    if (!isPreview || hasVideo || rendering || !(isAuto || isManual)) return;
    previewResumeFired.current = true;
    // Optimistically flip to the loader so there's no flash of the paywall gate.
    setProject((p) => p ? { ...p, order_status: 'in_progress', is_preview: false, approved_at: new Date().toISOString() } : p);
    void (async () => {
      try {
        if (isManual) await v2Service.renderManualPreview(userId, orderId);
        else await v2Service.renderAutoPreview(userId, orderId, projectProvider);
        void refreshUser();
        await refresh();
      } catch (e) {
        previewResumeFired.current = false;
        setProject((p) => p ? { ...p, order_status: 'preview', is_preview: true } : p);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((e as any)?.response?.status === 402) navigate('/subscription?context=insufficient_credits');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProUser, userId, orderId, isNew, loading, project?.is_preview, project?.order_status, hasVideo, rendering, isAuto, isManual]);

  // FREE PREVIEW funnel phase — ONE state machine for ALL modes (agent, auto, manual):
  // any order in 'preview' status gets the funnel. First arrival plays the 8s fake-load
  // (GeneratingPlayer) → blurred-preview gate. A refresh (intro already shown for this
  // order) jumps STRAIGHT to the gate, so the loading screen never replays. The per-order
  // intro flag is set the moment the load starts (so a refresh mid-load also skips the
  // replay). No mode gate: an agent Generate flips its order to 'preview' server-side,
  // and even an unknown-mode preview order still shows the gate instead of a dead-end.
  useEffect(() => {
    if (isProUser || !orderId || isNew || freeFunnel) return;
    const isPreview = !!(project?.is_preview || project?.order_status === 'preview');
    if (!isPreview) return;
    // A tier-2 lite project plays its plan steps first, then the same fast loader as tier 1.
    const firstPhase = project?.lite_funnel ? 'lite_plan' : 'generating';
    // This mount started the intro: an effect re-run (React StrictMode in dev) keeps playing it.
    if (introStartedRef.current === orderId) { setFreeFunnel(firstPhase); return; }
    const introKey = `vivideo_preview_intro_${orderId}`;
    let introShown = false;
    try { introShown = !!localStorage.getItem(introKey); } catch { /* private mode */ }
    if (introShown) {
      setFreeFunnel('preview');
    } else {
      try { localStorage.setItem(introKey, '1'); } catch { /* ignore */ }
      introStartedRef.current = orderId;
      setFreeFunnel(firstPhase);
    }
  }, [isProUser, orderId, isNew, freeFunnel, project?.is_preview, project?.order_status, project?.lite_funnel]);

  // Blurred backdrop for ANY PREVIEW order's paywall gate — generate a cheap cover
  // from the prompt once (idempotent on the order), so the project never looks empty.
  // Runs at preview time only (NOT at plan-arrival — most users who never press
  // Generate would make prefetching pure cost). Agent orders usually already have a
  // cover from the Generate press (ensureCover); this is the reload backstop.
  useEffect(() => {
    if (isProUser || !userId || !orderId || isNew) return;
    const isPreview = !!(project?.is_preview || project?.order_status === 'preview');
    if (!isPreview) return;
    if (project?.cover_image_url || project?.preview_image_url || coverGenRef.current === orderId) return;
    coverGenRef.current = orderId;
    void (async () => {
      try {
        const res = await v2Service.generatePlanCover({ userId, orderId, concept: project?.order_name || title });
        if (res.cover_image_url) setProject((p) => p ? { ...p, cover_image_url: res.cover_image_url } : p);
      } catch { coverGenRef.current = null; }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProUser, userId, orderId, isNew, project?.is_preview, project?.order_status, project?.cover_image_url, project?.preview_image_url]);

  return (
    <div className="h-screen flex bg-[#121218] relative overflow-hidden">
      {/* Project history (ChatGPT-style, but for projects — status dots = categories) */}
      <ProjectHistorySidebar open={historyOpen} onToggle={() => setHistoryOpen((v) => !v)} currentId={orderId} currentStatus={project?.order_status} />

      {/* Mobile backdrop — on phones the history sidebar is an overlay drawer; tap to close. */}
      {historyOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setHistoryOpen(false)} />
      )}

      {/* Main column */}
      <div className="relative z-10 flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="h-14 shrink-0 flex items-center justify-between gap-2 px-3 sm:px-4 border-b border-glass-border bg-surface/40 backdrop-blur-md">
          <div className="flex items-center gap-2 min-w-0">
            {!historyOpen && (
              <button onClick={() => setHistoryOpen(true)} className="w-9 h-9 rounded-lg flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 transition-colors" aria-label={t('agentProject.showProjects')}>
                <PanelLeft className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </button>
            )}
            <button onClick={() => navigate('/projects')} className="w-9 h-9 rounded-xl bg-gradient-to-br from-surface-light to-surface border border-glass-border flex items-center justify-center text-text-secondary hover:text-white hover:border-primary/50 transition-all" aria-label={t('agentProject.backToProjects')}>
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center px-3.5 py-1.5 rounded-full border border-glass-border bg-surface">
              <span className="text-sm font-semibold text-text-primary max-w-[40vw] truncate">{title}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button onClick={() => navigate(activeOrg ? '/team?tab=billing' : '/subscription')} className="flex items-center gap-2 px-4 py-2 rounded-3xl bg-surface border border-primary/30 hover:border-primary/50 transition-all group" title={t('agentProject.yourRemainingCredits')}>
              <span className="text-sm text-text-secondary hidden sm:inline">{activeOrg ? t('header.teamCredits') : t('agentProject.credits')}</span>
              <span className="px-3 py-1 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-bold tabular-nums shadow-[0_0_12px_rgba(139,92,246,0.4)]">{credits}</span>
            </button>
            <button onClick={() => void refreshCredits()} className="p-2 rounded-xl hover:bg-surface-light transition-colors" title={t('agentProject.refreshCredits')}>
              <RefreshCw className={cn('w-5 h-5 text-text-secondary', creditsRefreshing && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex min-h-0 justify-center">
          <div className="w-full max-w-4xl flex flex-col h-full min-h-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-text-tertiary text-sm gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t('agentProject.loading')}
              </div>
            ) : loadError && !project ? (
              <LoadErrorNote onRetry={() => { setLoadError(false); setLoading(true); void refresh(); }} />
            ) : isManual ? (
              /* Manual (fal) project — simple result view. */
              <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
                {/* The user's prompt, shown above the video for every manual order. */}
                <OrderPromptBlock orderId={orderId} prompt={project?.prompt} />
                {/* FREE: 8s fake-load (blurred fal cover) → blurred-preview gate → paywall.
                    NO real fal render ever happens for a free user — the order stays is_preview. */}
                {freeFunnel === 'generating' && (
                  <GeneratingPlayer durationSec={planDurationSec} blurImageUrl={freeFunnelImg} fast onDone={() => setFreeFunnel('preview')} />
                )}
                {/* Real fal render (paid, or after subscribe) */}
                {rendering && !freeFunnel && userId && orderId && <FalGeneratingPlayer userId={userId} orderId={orderId} startedAt={project?.created_date || undefined} falModel={project?.fal_model || undefined} />}
                {rendering && providerMessage && <ProviderNote message={providerMessage} />}
                {status === 'failed' && (refused || project?.error_message === 'content_refused'
                  ? <RefusalNote message={providerMessage} />
                  : <FailureNote error={project?.error_message} />)}
                {freeFunnel === 'preview' && !videoUrl && !rendering && (
                  <PreviewReadyGate
                    title={title}
                    mode="manual"
                    imageUrl={freeFunnelImg || project?.cover_image_url || project?.preview_image_url || undefined}
                    onSubscribe={() => navigate(`/subscription?context=video_ready&from_order=${orderId}`)}
                    onLockedAction={(a) => navigate(`/subscription?context=${a}&from_order=${orderId}`)}
                  />
                )}
                {videoUrl && (
                  <VideoResultCard
                    title={title}
                    videoUrl={videoUrl}
                    poster={project?.preview_image_url || undefined}
                    durationSec={project?.video_duration || undefined}
                    onOpen={() => setPlayerVideo({
                      title, videoUrl,
                      cleanUrl: project?.video_url || undefined,
                      burnedUrl: project?.output_url || undefined,
                      canRequestClean: cleanCopyOffered,
                      poster: project?.preview_image_url || undefined,
                      duration: project?.video_duration || undefined,
                    })}
                  />
                )}
                {/* Catch-all: an order in a state none of the branches above cover must
                    never render an EMPTY page (reported as "screen stays blank"). */}
                {!freeFunnel && !rendering && status !== 'failed' && !videoUrl && (
                  <div className="flex-1 flex items-center justify-center text-text-tertiary text-sm gap-2 py-16">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t('agentProject.loading')}
                  </div>
                )}
              </div>
            ) : isAuto ? (
              /* Auto-generate (one-shot HeyGen) — manual-style preview: NO chat, just
                 the 16× loader → the finished video (each press is its own project). */
              <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
                {/* FREE: 8s fake-load (blurred fal cover) → blurred-preview gate → paywall.
                    NO real HeyGen render ever happens for a free user — the order stays is_preview. */}
                {freeFunnel === 'generating' && (
                  <GeneratingPlayer durationSec={planDurationSec} blurImageUrl={freeFunnelImg} fast onDone={() => setFreeFunnel('preview')} />
                )}
                {/* Real HeyGen render (paid, or after subscribe) */}
                {rendering && !freeFunnel && (
                  <GeneratingPlayer
                    durationSec={Number(project?.approved_duration_sec) || planDurationSec}
                    approvedAt={project?.approved_at || undefined}
                  />
                )}
                {status === 'failed' && (refused || project?.error_message === 'content_refused'
                  ? <RefusalNote message={providerMessage} />
                  : <FailureNote error={project?.error_message} />)}
                {freeFunnel === 'preview' && !videoUrl && !rendering && (
                  <PreviewReadyGate
                    title={title}
                    mode="autogenerate"
                    imageUrl={freeFunnelImg || project?.cover_image_url || project?.preview_image_url || undefined}
                    onSubscribe={() => navigate(`/subscription?context=video_ready&from_order=${orderId}`)}
                    onLockedAction={(a) => navigate(`/subscription?context=${a}&from_order=${orderId}`)}
                  />
                )}
                {videoUrl && !rendering && (
                  <VideoResultCard
                    title={title}
                    videoUrl={videoUrl}
                    poster={project?.cover_image_url || project?.preview_image_url || undefined}
                    durationSec={project?.video_duration || undefined}
                    onOpen={() => setPlayerVideo({
                      title, videoUrl,
                      cleanUrl: project?.video_url || undefined,
                      burnedUrl: project?.output_url || undefined,
                      canRequestClean: cleanCopyOffered,
                      poster: project?.cover_image_url || project?.preview_image_url || undefined,
                      duration: project?.video_duration || undefined,
                    })}
                  />
                )}
                {/* The chat path shows these as turns; here they'd otherwise be lost. */}
                {isRouted && !rendering && !freeFunnel && <AssetNotes notes={providerAssetNotes(project)} />}
                {/* Catch-all: an order in a state none of the branches above cover must
                    never render an EMPTY page (reported as "screen stays blank"). */}
                {!freeFunnel && !rendering && status !== 'failed' && !videoUrl && (
                  <div className="flex-1 flex items-center justify-center text-text-tertiary text-sm gap-2 py-16">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t('agentProject.loading')}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Conversation */}
                <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-4 min-h-0">
                  {conv.map((turn, i) => {
                    if (turn.role === 'user') {
                      return (
                        <div key={i} className="flex flex-col items-end gap-1.5">
                          {turn.attachments && turn.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 justify-end max-w-[85%]">
                              {turn.attachments.map((a, k) => {
                                const Icon = a.type === 'image' ? ImageIcon : a.type === 'video' ? Film : a.type === 'audio' ? Music : FileText;
                                return (
                                  <span key={a.asset_id ?? k} className="flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full bg-surface-light border border-glass-border text-[11px] text-text-secondary">
                                    {a.type === 'image' && a.url
                                      ? <img src={a.url} alt="" className="w-4 h-4 rounded object-cover shrink-0" />
                                      : <Icon className="w-3.5 h-3.5 text-text-tertiary shrink-0" />}
                                    <span className="max-w-[120px] truncate">{a.name || 'file'}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {turn.text && (
                            <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-tr-md bg-surface-light border border-glass-border text-sm text-text-primary leading-relaxed whitespace-pre-wrap">{turn.text}</div>
                          )}
                        </div>
                      );
                    }
                    if (turn.kind === 'plan' && turn.plan) {
                      // A provider plan IS an approval card: it carries an approval_id, its
                      // own quoted price, and it locks once answered (no generating from an
                      // older one). Everything below stays exactly as it was for HeyGen.
                      const lvApprovalId = isRouted ? (turn.approval_id || '') : '';
                      const lvSettled = !!lvApprovalId && !!storyboardStateAt(i, turn);
                      // A provider plan with NO approval_id is the VIVIDEO (free) plan card:
                      // it is the free user's Generate, and it locks the moment they pay (the
                      // server resumes from the brief and the director re-plans).
                      const lvFreePlan = isRouted && !lvApprovalId;
                      const lvFreeLocked = lvFreePlan && !!turn.resolved;
                      // Every plan keeps a working Generate (generate from older plans too) —
                      // paused only while a render/funnel runs or a change awaits a response.
                      const actionable = !rendering && !generating && !freeFunnel && !awaitingChange && !lvSettled && !lvFreeLocked;
                      const isLatestPlan = i === latestPlanIdx;
                      // Stable key (the plan's own id, NOT the array index) so the card — and
                      // its open picker / in-card selections — survive a project refetch
                      // (persistSelection, background poll). An index key remounted the card,
                      // closing the voice picker mid-click and dropping the preview.
                      return (
                        <div key={turn.id ?? `plan-${i}`} id={turn.id ? `plan-${turn.id}` : undefined}>
                          <V2PlanCard
                            orderId={orderId || ''}
                            // PER-CARD cast: prefer THIS plan turn's own avatar/voice/brand;
                            // fall back to the project default (home composer) when this card
                            // hasn't been given an explicit pick.
                            plan={videoPlanToCardPlan(turn.plan, {
                              avatarId: turn.avatar_id ?? project?.avatar_id, avatarName: turn.avatar_name ?? project?.avatar_name, avatarImage: turn.avatar_image ?? project?.avatar_image,
                              voiceId: turn.voice_id ?? project?.voice_id, voiceName: turn.voice_name ?? project?.voice_name, voicePreviewUrl: turn.voice_preview_url ?? project?.voice_preview_url,
                              brandRef: turn.brand_ref ?? project?.brand_ref, brandName: turn.brand_name ?? project?.brand_name, brandLogo: turn.brand_logo ?? project?.brand_logo,
                            })}
                            actionable={actionable}
                            // A freshly drafted / revised plan opens EXPANDED. Collapse a plan
                            // only once it's superseded, or once a video has been generated FROM
                            // it (a video turn appears after it) — so revising after a video
                            // re-opens the new plan.
                            collapsedDefault={!isLatestPlan || latestVideoIdx > i || lvSettled || (lvFreePlan && !!turn.resolved)}
                            expandSignal={expandPlanId && expandPlanId === turn.id ? expandTick : 0}
                            busy={generating || !!freeFunnel}
                            // Free funnel steer: pulse the latest plan's Generate button so the
                            // next step is unmissable (the chat auto-scrolls the card into view).
                            glowGenerate={!isProUser && isLatestPlan && actionable}
                            // The provider prices its own card; HeyGen keeps the duration rule.
                            credits={lvApprovalId ? Math.round(Number(turn.credits) || 0) : undefined}
                            // Generate on a provider plan IS the approval — no HeyGen render call.
                            // A Vivideo plan card (free or LongVid) renders that plan itself.
                            onApprove={(_dur, avatarId, voiceId, brandRef) => void (lvApprovalId
                              ? approveStoryboard(i, lvApprovalId, turn.id)
                              : generate(turn.plan as V2VideoPlan, avatarId, voiceId, brandRef, turn.id))}
                            rawPlan={turn.plan}
                            // The provider's plan is not ours to rewrite — the user asks it in chat.
                            // A free plan card is ours, but only while it is still actionable.
                            editable={!lvApprovalId && (!lvFreePlan || actionable)}
                            declined={!!lvApprovalId && storyboardStateAt(i, turn) === 'declined'}
                            superseded={!!lvApprovalId && storyboardStateAt(i, turn) === 'superseded'}
                            // Edit only the latest plan (backend save replaces the latest card).
                            onSavePlan={isLatestPlan ? (edited) => void saveEditedPlan(i, edited) : undefined}
                            // In-card cast/brand picks persist to THIS card's own turn (per-card cast).
                            onSelectAsset={(patch) => persistSelection(turn.id, patch)}
                            // Free length cap on every provider; LongVid director cards keep their note.
                            lengthCap={isLongvid || !lvApprovalId}
                            onPlanFullLength={!lvApprovalId && canPlanFullLength({
                              isLatestPlan, hasVideo, completed: project?.order_status === 'completed',
                              failed: project?.order_status === 'failed',
                              rendering: rendering || !!freeFunnel || awaitingChange,
                            }) ? (text) => void send(text) : undefined}
                          />
                        </div>
                      );
                    }
                    if (turn.kind === 'event') {
                      // System note for a UI action (generated / edited). For a generate
                      // note tagged with a plan that still exists, make "this plan" a link
                      // that scrolls to + expands that exact card (confirm the source).
                      const planRef = turn.plan_ref || null;
                      const canLink = turn.event === 'generate' && !!planRef && conv.some((x) => x.kind === 'plan' && x.id === planRef);
                      // The backend writes this note as a locale KEY with empty text; resolve it
                      // (a LongVid generate note falls back to the same translated line) so no
                      // English reaches a non-English user.
                      const eventBody = turnBody(turn.i18n_key, turn.text);
                      const lvGenerate = isRouted && turn.event === 'generate';
                      return (
                        <div key={i} className="flex justify-center animate-fade-in">
                          <span className="text-[11px] text-text-tertiary bg-surface-light/40 border border-glass-border rounded-full px-3 py-1">
                            {canLink ? (
                              <>
                                {t('agentProject.generatingFrom')}{' '}
                                <button
                                  onClick={() => scrollToPlan(planRef)}
                                  className="font-bold text-primary hover:text-accent underline underline-offset-2 transition-colors"
                                >
                                  {t('agentProject.thisPlan')}
                                </button>.
                              </>
                            ) : turn.i18n_key && eventBody ? eventBody : lvGenerate ? (
                              <>{t('agentProject.generatingFrom')} {t('agentProject.thisPlan')}.</>
                            ) : turn.text}
                          </span>
                        </div>
                      );
                    }
                    if (turn.kind === 'storyboard' && turn.storyboard) {
                      // The provider's storyboard card. Live only for the latest
                      // unresolved card, and never while a production is running — the
                      // card locks its own expiry.
                      const sb = turn.storyboard;
                      const approvalId = sb.approval_id || turn.approval_id || '';
                      const sbResolved = storyboardStateAt(i, turn);
                      return (
                        <V2StoryboardCard
                          key={turn.id ?? `storyboard-${i}`}
                          storyboard={sb}
                          actionable={i === latestStoryboardIdx && !rendering && !generating && !sbResolved}
                          busy={generating}
                          resolved={sbResolved}
                          // Vivideo credits, priced server-side from the planned length.
                          credits={Math.round(Number(turn.credits) || 0)}
                          onApprove={() => void approveStoryboard(i, approvalId, turn.id)}
                          onDecline={() => void declineStoryboard(i, approvalId, turn.id)}
                        />
                      );
                    }
                    if (turn.kind === 'change' && turn.change) {
                      const resolved = changeStateAt(i, turn);
                      // A provider quote carries its price in Vivideo credits, an
                      // itemized breakdown and a 24h window; a HeyGen change keeps
                      // today's maths. Both are labelled the same way.
                      const lvExpiresAt = Number(turn.expires_at) || 0;
                      const lvExpired = isRouted && lvExpiresAt > 0 && Date.now() / 1000 >= lvExpiresAt;
                      const lvCredits = Math.round(Number(turn.credits) || 0);
                      return (
                        <V2ChangeCard
                          key={i}
                          summary={turn.change.summary}
                          credits={isRouted ? lvCredits : calculateHeygenCredits(Math.round(Number(project?.video_duration) || planDurationSec))}
                          items={turn.items}
                          // Live only while it's the latest change, a video exists, nothing's
                          // rendering, AND it isn't resolved — once approved/discarded it stays
                          // locked for good (never re-activates after the re-render).
                          actionable={i === latestChangeIdx && hasVideo && !rendering && !resolved && !lvExpired}
                          busy={generating}
                          resolved={resolved}
                          onApprove={() => void (isRouted
                            ? approveProviderChange(i, turn.approval_id || '', turn.id)
                            : approveChange(i, turn.change as V2AgentChange, turn.id))}
                          onReject={() => void (isRouted
                            ? declineProviderChange(i, turn.approval_id || '', turn.id)
                            : rejectChange(i, turn.id))}
                        />
                      );
                    }
                    if (turn.kind === 'video' && turn.video) {
                      // Drop duplicate videos from an accidental double-generate (see
                      // skipVideoIdx) — keep only the last video in an uninterrupted run.
                      if (skipVideoIdx.has(i)) return null;
                      // Every rendered video stays in the chat, inline & in order (a change
                      // re-render appends a NEW one below — none disappear). Click → rich player.
                      const vUrl = turn.video.output_url || turn.video.video_url || '';
                      if (!vUrl) return null;
                      const poster = project?.cover_image_url || project?.preview_image_url || undefined;
                      // The clean copy is made on the ORDER, so the turn showing the
                      // latest delivered video reads its urls live instead of from its
                      // own snapshot (identical values until a copy is made).
                      const latestDelivered = !!turn.video.output_url && turn.video.output_url === project?.output_url;
                      return (
                        <VideoResultCard
                          key={i}
                          title={title}
                          videoUrl={vUrl}
                          poster={poster}
                          durationSec={turn.video.duration || undefined}
                          onOpen={() => setPlayerVideo({
                            title,
                            videoUrl: vUrl,
                            cleanUrl: (latestDelivered ? project?.video_url : turn.video?.video_url) || undefined,
                            burnedUrl: turn.video?.output_url || undefined,
                            canRequestClean: latestDelivered && cleanCopyOffered,
                            poster,
                            duration: turn.video?.duration || undefined,
                          })}
                        />
                      );
                    }
                    // A server-chosen locale key (e.g. the insufficient-credits or
                    // could-not-start notice) wins over `text`, which those turns
                    // leave empty — so the notice is never English-only or blank.
                    const body = turnBody(turn.i18n_key, turn.text);
                    return body ? (
                      <p key={i} className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap"><MarkdownText text={body} /></p>
                    ) : null;
                  })}

                  {/* The branded thinking loader (baton + buzzwords) shows for EVERYONE
                      while a reply is in flight. The skeleton plan card joins it ONLY
                      during the FIRST plan (`planning`) — that response is always a plan
                      card. Follow-up turns can legitimately be a question, prose, or a
                      change card (especially for premium), so no skeleton there. */}
                  {(sending || planning) && <AgentThinking />}
                  {/* Skeleton = "your first plan is coming"; an async follow-up reply
                      (edit turn) shows only the thinking dots, not a phantom plan card. */}
                  {/* A LongVid order never produces a Vivideo plan (its director writes
                      one, current_plan stays null), so the skeleton would promise a card
                      that never comes — the thinking line above is the whole wait there.
                      Every other provider runs our planner, so the skeleton is honest. */}
                  {planning && !project?.current_plan && !isLongvid && <SkeletonPlanCard />}

                  {/* INLINE render loader: the chat (messages + plans + prior videos)
                      stays visible above; the composer is parked. Blurred player + 16× bar
                      (anchored to approved_at) + buzzwords. Covers the first generate AND a
                      change re-render (the prior video sits above as its own turn). */}
                  {rendering && !freeFunnel && (directing || preparing ? (
                    <AgentThinking label={preparing ? t('agentProject.stage.preparing') : undefined} />
                  ) : (
                    <GeneratingPlayer
                      durationSec={Number(project?.approved_duration_sec) || planDurationSec}
                      approvedAt={project?.approved_at || undefined}
                      // The provider reports real progress; every other order keeps the estimate.
                      progressPct={isRouted ? renderProgressPct(project) : undefined}
                    />
                  ))}
                  {/* The provider narrates its own stage; the HeyGen chat never sets
                      providerMessage here, so this only shows for those orders. */}
                  {isRouted && rendering && !directing && !preparing && !freeFunnel && providerMessage && <ProviderNote message={providerMessage} />}

                  {/* Tier-2 lite project: generic plan steps where tier 1 waits for its plan card;
                      once done they stay, finished, above the loader and the preview. */}
                  {project?.lite_funnel && (freeFunnel === 'lite_plan' || freeFunnel === 'generating' || freeFunnel === 'preview') && (
                    <LitePlanSteps completed={freeFunnel !== 'lite_plan'} onDone={() => setFreeFunnel('generating')} />
                  )}
                  {/* FREE funnel: fast "generation" → blurred preview + paywall. */}
                  {freeFunnel === 'generating' && (
                    <GeneratingPlayer durationSec={planDurationSec} blurImageUrl={freeFunnelImg} fast onDone={() => setFreeFunnel('preview')} />
                  )}
                  {freeFunnel === 'preview' && (
                    <PreviewReadyGate
                      title={title}
                      mode="agentic"
                      // Fall back to the order-stored cover: after a reload the local
                      // ensureCover result is gone but the cover persists on the order.
                      imageUrl={freeFunnelImg || project?.cover_image_url || project?.preview_image_url || undefined}
                      onSubscribe={() => navigate(`/subscription?context=video_ready&from_order=${orderId}`)}
                      onLockedAction={(a) => navigate(`/subscription?context=${a}&from_order=${orderId}`)}
                      compact
                    />
                  )}

                  {status === 'failed' && (refused || project?.error_message === 'content_refused'
                  ? <RefusalNote message={providerMessage} />
                  : <FailureNote error={project?.error_message} />)}

                  <div ref={bottomRef} />
                </div>

                {/* Composer */}
                <div className="px-3 sm:px-4 pb-4 shrink-0">
                  {rendering || freeFunnel === 'generating' || freeFunnel === 'lite_plan' || planning ? (
                    <div className="rounded-2xl bg-surface-light/40 border border-glass-border p-3.5 flex items-center gap-3 text-text-tertiary">
                      <Loader2 className="w-4 h-4 animate-spin shrink-0 text-primary" />
                      <span className="text-sm">{preparing ? t('agentProject.stage.preparing') : planning || directing || freeFunnel === 'lite_plan' ? t('agentProject.puttingPlanTogether') : freeFunnel === 'generating' ? t('agentProject.preparingVideo') : t('agentProject.videoRendering')}</span>
                    </div>
                  ) : freeFunnel === 'preview' ? (
                    /* Free funnel DONE — the fake render finished and the blurred preview + paywall
                       is shown above. Park the composer with a subscribe steer (NOT the stuck
                       "preparing your video…" loader, which used to linger because 'preview' is
                       also truthy on freeFunnel). */
                    <button
                      onClick={() => navigate(`/subscription?context=video_ready&from_order=${orderId}`)}
                      className="w-full rounded-2xl bg-primary/5 border border-primary/30 p-3.5 flex items-center gap-3 text-left hover:border-primary/50 transition-colors"
                    >
                      <Sparkles className="w-4 h-4 text-primary shrink-0" />
                      <span className="flex-1 text-sm text-text-secondary">{t('agentProject.previewReadySubscribe')}</span>
                    </button>
                  ) : (awaitingChange || providerChatLocked) ? (
                    /* A change (or a provider QUOTE) is on the table → chat is parked until
                       they answer it. A pending storyboard leaves the composer open. */
                    <div className="rounded-2xl bg-accent/5 border border-accent/30 p-3.5 flex items-center gap-3 text-text-secondary">
                      <span className="w-2.5 h-2.5 rounded-full bg-accent animate-pulse shrink-0" />
                      <span className="text-sm">{providerChatLocked ? t('agentProject.longvidWaitingApproval') : t('agentProject.approveOrDiscardChange')}</span>
                    </div>
                  ) : freeChatLimitReached ? (
                    /* Chat cap reached (free) → steer to Generate (primary; the app keeps
                       reading as free — the paywall appears only at the video gate), plus a
                       SUBTLE one-line Pro mention for the revision-hungry. */
                    <div className="rounded-2xl bg-primary/5 border border-primary/30 p-3.5">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-4 h-4 text-primary shrink-0" />
                        <p className="flex-1 text-sm text-text-secondary leading-relaxed">
                          {t('agentProject.planReadyPressPrefix')} <span className="font-semibold text-text-primary">{t('agentProject.generate')}</span> {t('agentProject.planReadyPressSuffix')}
                        </p>
                      </div>
                      <button
                        onClick={() => navigate(`/subscription?context=free_chat_limit&from_order=${orderId}`)}
                        className="mt-1.5 ml-7 text-xs text-text-tertiary hover:text-text-secondary underline underline-offset-2 transition-colors"
                      >
                        {t('agentProject.freeChatLimitUpsell')}
                      </button>
                    </div>
                  ) : (
                    <>
                      {providerAwaitingInput && (
                        <div className="flex items-center gap-2 mb-2.5 px-1 text-text-secondary">
                          <span className="w-2 h-2 rounded-full bg-accent animate-pulse shrink-0" />
                          <span className="text-sm">{t('agentProject.longvidWaitingForAnswer')}</span>
                        </div>
                      )}
                      {/* Post-video edit ideas — one tap AUTOFILLS the composer (never sends). */}
                      {hasVideo && (
                        <div className="flex items-center gap-2 mb-2.5 overflow-x-auto scrollbar-hide">
                          {FOLLOWUP_SUGGESTIONS.map((chip) => (
                            <button
                              key={chip}
                              onClick={() => { setInput(chip); composerRef.current?.focus(); }}
                              disabled={sending}
                              className="shrink-0 px-3 py-1.5 rounded-full bg-surface-light border border-glass-border text-xs font-medium text-text-primary hover:border-primary/40 hover:shadow-glow-sm transition-all whitespace-nowrap disabled:opacity-50"
                            >
                              {chip}
                            </button>
                          ))}
                        </div>
                      )}
                    <div className="rounded-2xl bg-surface-light/70 border border-glass-border p-3 flex items-center gap-2">
                      <input
                        ref={composerRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void send(input); }}
                        placeholder={hasVideo ? t('agentProject.composerPlaceholderHasVideo') : t('agentProject.composerPlaceholder')}
                        className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary outline-none px-1"
                        disabled={sending}
                      />
                      <button
                        onClick={() => void send(input)}
                        disabled={sending || !input.trim()}
                        className="px-4 py-1.5 rounded-full bg-primary hover:bg-primary/90 text-sm font-semibold text-white transition-colors disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                      >
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {t('agentProject.send')}
                      </button>
                    </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Rich video player for whichever video the user opened (each render persists,
          so any of them can be reopened): captions on/off toggle (swaps clean ⇄
          captioned), Download, and Share (copy link + social). */}
      {playerVideo && (
        <VideoPlayerModal
          title={playerVideo.title}
          videoUrl={playerVideo.videoUrl}
          id={orderId && orderId !== 'new' ? orderId : undefined}
          meta={playerVideo.duration ? t('agentProject.videoMeta', { seconds: Math.round(Number(playerVideo.duration)) }) : 'Vivideo'}
          cleanUrl={playerVideo.cleanUrl}
          burnedUrl={playerVideo.burnedUrl}
          onRequestClean={playerVideo.canRequestClean ? requestCleanCopy : undefined}
          poster={playerVideo.poster}
          plan={project?.current_plan || [...(project?.conversation || [])].reverse().find((tn) => tn.plan)?.plan || null}
          orderId={orderId && orderId !== 'new' ? orderId : undefined}
          // The A/B analytics provider is the VIDEO's own, never the user's current
          // arm — an old HeyGen video reopened by a LongVid user must not read LongVid.
          videoProvider={project?.video_provider ?? null}
          fallbackFrom={project?.fallback_from ?? null}
          // approved_at dates the project's current video only; an older render is left undated.
          approvedAt={playerVideo.videoUrl === (project?.output_url || project?.video_url) ? project?.approved_at ?? null : null}
          durationSec={playerVideo.duration}
          // Ratings are asked for the project's current video only, and only a newly made one.
          canRate={isRateable(project?.approved_at) && playerVideo.videoUrl === (project?.output_url || project?.video_url)}
          // Closing retires the clean-copy run, so its poll stops with the player.
          onClose={() => { cleanRunRef.current += 1; setPlayerVideo(null); }}
        />
      )}
    </div>
  );
}

/** HeyGen declined the request. Distinct from FailureNote: nothing went wrong
 *  technically, credits are already refunded, and retrying the same prompt in
 *  this chat cannot work — so we do NOT offer a retry here. */
function RefusalNote({ message }: { message?: string | null }) {
  const { t } = useTranslation('v2');
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 animate-fade-in">
      <p className="text-sm font-semibold text-amber-200">{t('moderation.refusedTitle')}</p>
      <p className="text-xs text-amber-100/80 mt-1 leading-relaxed">{t('moderation.refused')}</p>
      {message && (
        <p className="mt-2 text-[11px] text-amber-100/60 bg-black/20 rounded-lg p-2 whitespace-pre-wrap break-words">
          <span className="font-semibold">{t('moderation.assistant')}: </span>{message}
        </p>
      )}
    </div>
  );
}

/** HeyGen's own progress narration while a render is in flight. A long render
 *  used to look identical to a hang; this makes the wait legible. */
function ProviderNote({ message }: { message?: string | null }) {
  const { t } = useTranslation('v2');
  if (!message) return null;
  return (
    <p className="text-xs text-text-tertiary leading-relaxed px-1 animate-fade-in">
      <span className="font-semibold text-text-secondary">{t('moderation.assistant')}: </span>{message}
    </p>
  );
}

/** What the provider could not use for this video — a quiet note under the
 *  result on the views that have no chat to put it in. */
function AssetNotes({ notes }: { notes?: string[] | null }) {
  const lines = assetNoteLines(notes);
  if (!lines.length) return null;
  return (
    <div className="rounded-xl border border-glass-border bg-surface-light/40 px-3 py-2.5 space-y-1 animate-fade-in">
      {lines.map((line, i) => (
        <p key={i} className="text-xs text-text-tertiary leading-relaxed">{line}</p>
      ))}
    </div>
  );
}

/** The project could not be loaded at all (network, 4xx). Never a spinner: the
 *  user needs to know it failed and be able to try again. */
function LoadErrorNote({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('v2');
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-sm font-semibold text-text-primary">{t('agentProject.somethingWentWrong')}</p>
      <p className="text-xs text-text-tertiary leading-relaxed">{t('agentProject.couldNotLoadProject')}</p>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-surface-light border border-glass-border text-sm font-semibold text-text-primary hover:border-primary/40 transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" /> {t('agentProject.tryAgain')}
      </button>
    </div>
  );
}

function FailureNote({ error }: { error?: string | null }) {
  const { t } = useTranslation('v2');
  // The reason is a locale key on provider orders and raw text on HeyGen ones.
  const detail = errorText(error);
  return (
    <div className="rounded-2xl border border-red-500/25 bg-red-500/5 p-4 animate-fade-in">
      <p className="text-sm font-semibold text-text-primary">{t('agentProject.somethingWentWrong')}</p>
      <p className="text-xs text-text-tertiary mt-1 leading-relaxed">{t('agentProject.couldNotFinishVideo')}</p>
      {detail && (
        <details className="mt-2">
          <summary className="text-[11px] text-text-tertiary cursor-pointer hover:text-text-secondary select-none">{t('agentProject.showDetails')}</summary>
          <p className="mt-1.5 text-[11px] text-text-tertiary/80 bg-black/20 rounded-lg p-2 whitespace-pre-wrap break-words">{detail}</p>
        </details>
      )}
    </div>
  );
}

